/**
 * FileEntry displays a single file (document) and related actions
 */
import React, { Component } from "react";
import filesize from "filesize";
import { IconButton, Tooltip, withStyles } from "@material-ui/core";
import { Delete, Download, OpenInNew } from "mdi-material-ui";

import locale from "@bonliva-crm/locale";
import { Classes, ThemeType } from "@theme";
import { getIconByFilename } from "@utils/fileUtils";
import InlineSelect from "@components/InlineSelect";
import { Overlay } from "@components";
import { Document } from "./utils";
import { store } from "@store";

export type DocumentEntryProps = {
  classes: Classes<typeof styles>;
  data: Document;
  error?: boolean | string;
  showFileIcon?: boolean;
  disableDocument?: boolean;
  extraContentAfterName?: React.ReactNode;
  onDownload?: (e: React.SyntheticEvent<any>) => void;
  onDocumentTypeChange?: (type: string) => void;
  onOpen?: (e: React.SyntheticEvent<any>) => void;
  onDelete?: (r: React.SyntheticEvent<any>) => void;
  hideDelete?: boolean;
  disabledOnDelete?: boolean;
  disabledOnDeleteTooltip?: string;
  /** prevent document type inline select from autoopening */
  preventSelectAutoOpen?: boolean;
  /** Document types (e.g. IVO, HOSP, etc) */
  documentTypes?: string[];
  style?: React.CSSProperties;
};

class DocumentEntryCmp extends Component<DocumentEntryProps> {
  static defaultProps = {
    documentTypes: []
  };

  render() {
    const { classes, data, disableDocument } = this.props;

    const icon = this.props.showFileIcon ? getIconByFilename(data.FileRef.Name) : null;
    const showIcons = this.props.onDownload || this.props.onOpen || this.props.onDelete;

    const handleDelete = (e: React.SyntheticEvent<any>) => {
      e.preventDefault();
      store().ui.confirmDelete(locale.RemoveFile, (v) => {
        if (v) {
          this.props.onDelete(e);
        }
      });
    };

    return (
      <div className={classes.root} style={this.props.style}>
        <Overlay in={disableDocument} normal transparent />

        {this.props.showFileIcon && icon && <div className={classes.icon}>{icon}</div>}

        <div>
          <div
            title={data.FileRef.Name}
            className={classes.link}
            onClick={this.props.onDownload}
          >
            <div className={classes.filename}>{data.FileRef.Name}</div>
            {this.props.onDownload && (
              <div className={classes.filesize}>({this.getSize()})</div>
            )}
          </div>
        </div>

        {this.props.onDocumentTypeChange && (
          <div>
            <InlineSelect
              autoFocus
              autoOpen={!this.props.preventSelectAutoOpen}
              error={Boolean(this.props.error)}
              inputProps={{ style: { fontWeight: 600 } }}
              onChange={(e) =>
                this.props.onDocumentTypeChange &&
                this.props.onDocumentTypeChange(e.target.value as string)
              }
              options={this.props.documentTypes.toLabelValue()}
              disableUnderline
              value={data.DocumentType}
            />
          </div>
        )}

        {this.props.extraContentAfterName}

        <div className={classes.placeholder} />

        {showIcons && (
          <div className={classes.icons}>
            {this.props.onDownload && (
              <Tooltip title={locale.Download}>
                <IconButton
                  onClick={this.props.onDownload}
                  disabled={this.props.disableDocument}
                  size="small"
                >
                  <Download fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {this.props.onOpen && (
              <Tooltip title="View in a new window">
                <IconButton
                  onClick={this.props.onOpen}
                  disabled={this.props.disableDocument}
                  size="small"
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {this.props.onDelete && !this.props.hideDelete && (
              <Tooltip
                title={
                  this.props.disabledOnDelete
                    ? this.props.disabledOnDeleteTooltip
                    : locale.Delete
                }
              >
                <IconButton
                  onClick={(e) => (this.props.disabledOnDelete ? null : handleDelete(e))}
                  disabled={this.props.disabledOnDelete}
                  size="small"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    );
  }

  getSize = () => {
    const { data } = this.props;

    if (typeof data.FileRef.Size !== "number") {
      return null;
    }

    return filesize(data.FileRef.Size, { round: 0 });
  };
}

const styles = (theme: ThemeType) => ({
  root: {
    ...theme.mixins.childrenMarginRight(theme.spacing(1 / 2)),
    alignItems: "center",
    display: "flex",
    position: "relative"
  },

  icon: {
    // marginBottom: 4
  },

  link: {
    display: "flex",
    alignItems: "baseline",
    cursor: "pointer"
  },

  filename: {
    maxWidth: 400,
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  filesize: {
    fontSize: theme.typography.fontSize * 0.65,
    paddingLeft: 4,
    paddingRight: 4
  },

  placeholder: {
    borderBottom: `1px dashed ${theme.separatorColor}`,
    flex: "1 0 0",
    marginBottom: -6
  },

  size: {
    whiteSpace: "nowrap"
  },

  icons: {
    ...theme.mixins.childrenMarginRight(theme.spacing(1 / 2)),
    display: "flex",
    alignItems: "center",
    whiteSpace: "nowrap",

    "& > *": {
      cursor: "pointer",
      color: theme.palette.grey[700],
      transition: theme.transitions.create("color")
    },
    "& > *:hover, & > *:active": {
      color: theme.palette.primary.main
    }
  }
});

export default withStyles(styles as any)(DocumentEntryCmp);
